/**
 * Calibration sweep (phase 5): run every tier for a fixed time on one
 * device and record what it costs.
 *
 * For each tier, lowest first: warm up (shader compile, JIT, buffers
 * settling), then measure. Per frame it records the rAF interval, the
 * main-thread CPU time of update + render, and the latest GPU time from
 * timestamp queries when the device supports them. If a tier's median
 * interval exceeds ABORT_MS, higher tiers are skipped: they would only be
 * slower, and a sweep that takes minutes on a weak device is a sweep
 * nobody finishes.
 *
 * Output is plain JSON meant to be pasted back into the repo
 * (bench/calibration/). Nothing is estimated: a value the device could not
 * measure is null.
 */
import { TIERS, TIER_ORDER, type TierId } from '../config/tiers';

export const SWEEP_WARMUP_MS = 4_000;
export const SWEEP_MEASURE_MS = 12_000;
const ABORT_MS = 100;
/** Enough for 12 s at 240 Hz. */
const SAMPLE_CAPACITY = 3_000;

export interface SweepTierResult {
  tier: TierId;
  debris: number;
  renderScale: number;
  frames: number;
  intervalMedianMs: number | null;
  intervalP95Ms: number | null;
  cpuMedianMs: number | null;
  cpuP95Ms: number | null;
  gpuMedianMs: number | null;
  gpuP95Ms: number | null;
  skipped: string | null;
}

export interface SweepDevice {
  userAgent: string;
  backend: string;
  physics: string;
  gpuTiming: boolean;
  hardwareConcurrency: number | null;
  deviceMemoryGb: number | null;
  devicePixelRatio: number;
  canvasCssPx: [number, number];
}

export interface SweepResult {
  version: 1;
  date: string;
  warmupMs: number;
  measureMs: number;
  device: SweepDevice;
  tiers: SweepTierResult[];
}

function quantile(sorted: Float64Array, n: number, q: number): number | null {
  if (n === 0) return null;
  return sorted[Math.min(n - 1, Math.floor(q * n))] as number;
}

export class BenchSweep {
  private readonly interval = new Float64Array(SAMPLE_CAPACITY);
  private readonly cpu = new Float64Array(SAMPLE_CAPACITY);
  private readonly gpu = new Float64Array(SAMPLE_CAPACITY);
  private n = 0;
  private nGpu = 0;
  private idx = 0;
  private phaseStartMs = -1;
  private measuring = false;
  private readonly results: SweepTierResult[] = [];
  done = false;

  /** Tier the sweep wants active right now. */
  get tier(): TierId {
    return TIER_ORDER[Math.min(this.idx, TIER_ORDER.length - 1)] as TierId;
  }

  /** Short status for the overlay. */
  get status(): string {
    if (this.done) return 'Sweep finished.';
    const phase = this.measuring ? 'measuring' : 'warming up';
    return `Calibration sweep: tier ${this.tier} (${this.idx + 1}/${TIER_ORDER.length}), ${phase}…`;
  }

  /**
   * Feed one frame. Returns the tier to switch to when the sweep moves on,
   * otherwise null. Allocation-free except at tier boundaries.
   */
  frame(nowMs: number, intervalMs: number, cpuMs: number, gpuMs: number): TierId | null {
    if (this.done) return null;
    if (this.phaseStartMs < 0) this.phaseStartMs = nowMs;
    const elapsed = nowMs - this.phaseStartMs;

    if (!this.measuring) {
      if (elapsed >= SWEEP_WARMUP_MS) {
        this.measuring = true;
        this.phaseStartMs = nowMs;
        this.n = 0;
        this.nGpu = 0;
      }
      return null;
    }

    if (this.n < SAMPLE_CAPACITY) {
      this.interval[this.n] = intervalMs;
      this.cpu[this.n] = cpuMs;
      this.n++;
      if (!Number.isNaN(gpuMs) && this.nGpu < SAMPLE_CAPACITY) this.gpu[this.nGpu++] = gpuMs;
    }
    if (elapsed < SWEEP_MEASURE_MS) return null;

    const r = this.finishTier();
    this.results.push(r);
    this.idx++;
    const tooSlow = r.intervalMedianMs !== null && r.intervalMedianMs > ABORT_MS;
    if (this.idx >= TIER_ORDER.length || tooSlow) {
      for (let i = this.idx; i < TIER_ORDER.length; i++) {
        const id = TIER_ORDER[i] as TierId;
        this.results.push({
          tier: id,
          debris: TIERS[id].maxDebris,
          renderScale: TIERS[id].renderScale,
          frames: 0,
          intervalMedianMs: null,
          intervalP95Ms: null,
          cpuMedianMs: null,
          cpuP95Ms: null,
          gpuMedianMs: null,
          gpuP95Ms: null,
          skipped: `previous tier median interval above ${ABORT_MS} ms`,
        });
      }
      this.done = true;
      return null;
    }
    this.measuring = false;
    this.phaseStartMs = nowMs;
    return this.tier;
  }

  result(device: SweepDevice): SweepResult {
    return {
      version: 1,
      date: new Date().toISOString(),
      warmupMs: SWEEP_WARMUP_MS,
      measureMs: SWEEP_MEASURE_MS,
      device,
      tiers: this.results.slice(),
    };
  }

  private finishTier(): SweepTierResult {
    const id = this.tier;
    const iv = this.interval.subarray(0, this.n).sort();
    const cp = this.cpu.subarray(0, this.n).sort();
    const gp = this.gpu.subarray(0, this.nGpu).sort();
    return {
      tier: id,
      debris: TIERS[id].maxDebris,
      renderScale: TIERS[id].renderScale,
      frames: this.n,
      intervalMedianMs: quantile(iv, this.n, 0.5),
      intervalP95Ms: quantile(iv, this.n, 0.95),
      cpuMedianMs: quantile(cp, this.n, 0.5),
      cpuP95Ms: quantile(cp, this.n, 0.95),
      gpuMedianMs: quantile(gp, this.nGpu, 0.5),
      gpuP95Ms: quantile(gp, this.nGpu, 0.95),
      skipped: null,
    };
  }
}
